import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api, formatApiError, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles, Send, Plus, Trash2, Copy, RotateCcw, Search, X,
  ImagePlus, Mic, MicOff, MessageCircle, ChevronDown, ChevronUp, Info, Menu
} from 'lucide-react';
import { toast } from 'sonner';
import MarkdownMessage from '@/components/MarkdownMessage';
import { GiulioMemory } from '@/components/GiulioMemory';
import { useGiulioContext, pathToLabel } from '@/context/GiulioContext';

const MAX_IMAGES = 4;
const MAX_DIM = 1600;
const JPEG_QUALITY = 0.85;

// --- utility compress ---
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Impossibile leggere l'immagine"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          const r = Math.min(MAX_DIM / width, MAX_DIM / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY) });
      };
      img.onerror = () => reject(new Error('Immagine non valida'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// --- Session card ---
function SessionItem({ s, active, onOpen, onDelete }) {
  return (
    <div className={`group flex items-start gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition ${active ? 'bg-secondary' : 'hover:bg-secondary/50'}`}
         onClick={()=>onOpen(s.session_id)}
         data-testid={`ai-session-${s.session_id}`}>
      <MessageCircle size={14} className="mt-0.5 shrink-0 text-muted-foreground"/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{s.title}</div>
        <div className="text-[10px] text-muted-foreground truncate">{s.last_message}</div>
      </div>
      <button
        onClick={(e)=>{ e.stopPropagation(); onDelete(s.session_id); }}
        className="opacity-0 group-hover:opacity-100 transition p-1 hover:bg-background rounded"
        aria-label="Elimina"
        data-testid={`ai-session-delete-${s.session_id}`}
      ><Trash2 size={12} className="text-muted-foreground"/></button>
    </div>
  );
}

// --- Message bubble with actions ---
function Bubble({ m, i, onCopy, onRegenerate, isLastAssistant }) {
  const isUser = m.role === 'user';
  return (
    <div className={`group flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[90%] md:max-w-[80%] rounded-2xl px-4 py-3 text-sm ${isUser ? 'bg-foreground text-background' : 'bg-secondary border border-border'}`}
           data-testid={`ai-msg-${m.role}-${i}`}>
        {m._attachments?.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            {m._attachments.map((a, ai) => (
              <img key={ai} src={a.dataUrl} alt="allegato" className="rounded-lg w-full h-28 object-cover border border-background/20"/>
            ))}
          </div>
        )}
        {m.images_count > 0 && !m._attachments && (
          <div className="mb-2 text-[10px] opacity-70 italic">📎 {m.images_count} immagine/i allegata/e</div>
        )}
        {isUser
          ? <div className="whitespace-pre-wrap break-words">{m.content}</div>
          : (
            <>
              <MarkdownMessage content={m.content}/>
              {m._streaming && (
                <span className="inline-block w-1.5 h-3.5 bg-chartreuse ml-0.5 -mb-0.5 animate-pulse rounded-sm"/>
              )}
            </>
          )}
        {!isUser && (
          <div className="mt-3 pt-2 border-t border-border/60 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            <button
              onClick={()=>onCopy(m.content)}
              className="text-[11px] px-2 py-1 rounded hover:bg-background inline-flex items-center gap-1"
              aria-label="Copia"
            ><Copy size={11}/> Copia</button>
            {isLastAssistant && (
              <button
                onClick={onRegenerate}
                className="text-[11px] px-2 py-1 rounded hover:bg-background inline-flex items-center gap-1"
                aria-label="Rigenera"
              ><RotateCcw size={11}/> Rigenera</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiAssistant() {
  const { context: giulioContext, clearContext } = useGiulioContext();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [processingImage, setProcessingImage] = useState(false);
  const [streamingIdx, setStreamingIdx] = useState(-1); // indice del messaggio in streaming
  const [streamingText, setStreamingText] = useState('');
  const abortRef = useRef(null);  // callback per interrompere lo stream fetch
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [openGroup, setOpenGroup] = useState(0);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [recording, setRecording] = useState(false);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  const loadSessions = useCallback(() => api.get('/ai/chat/sessions').then(r => setSessions(r.data || [])), []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { api.get('/ai/prompt-templates').then(r => setTemplates(r.data || [])); }, []);
  useEffect(() => {
    if (sessionId) api.get(`/ai/chat/${sessionId}`).then(r => setMessages(r.data || []));
    else setMessages([]);
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length, busy, streamingText]);

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    const rem = MAX_IMAGES - attachments.length;
    if (rem <= 0) { toast.error(`Max ${MAX_IMAGES} immagini`); return; }
    setProcessingImage(true);
    try {
      const out = [];
      for (const f of Array.from(fileList).slice(0, rem)) {
        if (!f.type.startsWith('image/')) continue;
        out.push(await compressImage(f));
      }
      setAttachments(a => [...a, ...out].slice(0, MAX_IMAGES));
    } catch (e) { toast.error(e.message || 'Errore'); }
    finally { setProcessingImage(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const removeAttachment = (i) => setAttachments(a => a.filter((_, x) => x !== i));

  const send = async (msg) => {
    const content = (msg || text).trim();
    if (!content && attachments.length === 0) return;
    setBusy(true);
    const imgs = attachments.map(a => a.dataUrl);
    const userMsg = { role: 'user', content: content || '(immagine allegata)',
      created_at: new Date().toISOString(), _attachments: [...attachments] };
    setMessages(m => [...m, userMsg]);
    setText(''); setAttachments([]);

    // Aggiungi bubble assistente vuoto che verrà riempito token-by-token via SSE
    let asstIdx = -1;
    setMessages(m => {
      const arr = [...m, { role: 'assistant', content: '', created_at: new Date().toISOString(), _streaming: true }];
      asstIdx = arr.length - 1;
      setStreamingIdx(asstIdx);
      return arr;
    });

    const wasFirst = !sessionId;
    try {
      const payload = { message: content || 'Analizza le immagini allegate.', session_id: sessionId };
      if (imgs.length) payload.images = imgs;
      // Context injection: se Giulio sta "guardando" un terreno/partita, lo manda al backend
      if (giulioContext && giulioContext.kind) {
        if (giulioContext.kind === 'listing' && giulioContext.data?.listing_id) {
          payload.page_context = { kind: 'listing', listing_id: giulioContext.data.listing_id };
        } else if (giulioContext.kind === 'batch' && giulioContext.data?.batch_id) {
          payload.page_context = { kind: 'batch', batch_id: giulioContext.data.batch_id };
        }
      }
      payload.page_path = location.pathname;
      const token = localStorage.getItem('agn_token');
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let sid = sessionId;
      abortRef.current = () => { try { reader.cancel(); } catch {} };
      let streamErr = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE messages separati da \n\n
        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // ultima parte incompleta rimane in buffer
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'start') {
              sid = ev.session_id;
              // NB: NON chiamare setSessionId qui — triggererebbe il useEffect
              // che ricarica messages dal DB e sovrascriverebbe la bubble in streaming.
              // setSessionId viene chiamato solo al 'done'.
              setBusy(false);
            } else if (ev.type === 'tool') {
              if (!acc) setStreamingText(`_${ev.label}…_`);
            } else if (ev.type === 'delta') {
              acc += ev.text;
              setStreamingText(acc);
            } else if (ev.type === 'done') {
              // Finalizza bubble → poi setSessionId (senza rehydrate perché messages è già ok)
              setMessages(m => m.map(mm => mm._streaming ? { ...mm, content: acc, _streaming: false } : mm));
              setStreamingIdx(-1); setStreamingText(''); setBusy(false);
              if (sid !== sessionId) setSessionId(sid);  // solo se cambiata (prima chat)
              if (wasFirst && sid) api.post(`/ai/chat/${sid}/title`).finally(loadSessions);
              else loadSessions();
            } else if (ev.type === 'error') {
              streamErr = ev.error || 'Errore streaming';
            }
          } catch (parseErr) {
            // ignora parse errors su chunk mal-formati (rari con SSE)
          }
        }
        if (streamErr) throw new Error(streamErr);
      }
      // se lo stream chiude senza 'done', finalizza comunque
      setMessages(m => m.map(mm => mm._streaming ? { ...mm, content: acc || mm.content, _streaming: false } : mm));
      setStreamingIdx(-1); setStreamingText(''); setBusy(false);
    } catch (e) {
      setMessages(m => m.filter(mm => !mm._streaming));
      setStreamingIdx(-1); setStreamingText('');
      setBusy(false);
      toast.error(e.message || 'Errore streaming AI');
    }
  };

  // Cleanup timer on unmount
  useEffect(() => () => { try { abortRef.current && abortRef.current(); } catch {} }, []);

  const stopStreaming = () => {
    // Interrompi lo streaming SSE lato client — quello che è stato ricevuto viene salvato
    try { abortRef.current && abortRef.current(); } catch {}
    setMessages(m => m.map(mm => mm._streaming ? { ...mm, content: streamingText || mm.content, _streaming: false } : mm));
    setStreamingIdx(-1);
    setStreamingText('');
    setBusy(false);
    loadSessions();
  };

  const regenerate = async () => {
    if (busy || messages.length < 2) return;
    // Trova ultimo messaggio user
    const lastUserIdx = [...messages].reverse().findIndex(m => m.role === 'user');
    if (lastUserIdx === -1) return;
    const idx = messages.length - 1 - lastUserIdx;
    const lastUserMsg = messages[idx];
    // Rimuovi solo l'ultima risposta assistant per rigenerare
    setMessages(m => m.slice(0, idx + 1));
    await send(lastUserMsg.content);
  };

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success('Copiato negli appunti'); };

  const newChat = () => { setSessionId(null); setMessages([]); setAttachments([]); setMobileSidebar(false); };
  const deleteSession = async (id) => {
    if (!confirm('Eliminare questa conversazione? L\'azione è irreversibile.')) return;
    await api.delete(`/ai/chat/${id}`);
    if (id === sessionId) newChat();
    loadSessions();
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Il tuo browser non supporta l\'input vocale'); return; }
    const rec = new SR();
    rec.lang = 'it-IT'; rec.continuous = false; rec.interimResults = true;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('');
      setText(prev => prev ? `${prev} ${t}` : t);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => { setRecording(false); toast.error('Errore microfono'); };
    recognitionRef.current = rec;
    rec.start(); setRecording(true);
  };
  const stopVoice = () => { recognitionRef.current?.stop(); setRecording(false); };

  const filteredSessions = sessions.filter(s => !search ||
    s.title?.toLowerCase().includes(search.toLowerCase()) ||
    s.last_message?.toLowerCase().includes(search.toLowerCase()));

  const hasMessages = messages.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-6 md:py-10">
      <div className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-6 min-h-[calc(100vh-180px)]">

        {/* ==== SIDEBAR ==== */}
        <aside className={`${mobileSidebar ? 'block fixed inset-0 z-50 bg-background p-4 md:relative md:p-0 md:block' : 'hidden md:block'}`}>
          <div className="rounded-2xl border border-border bg-card p-4 md:sticky md:top-24">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Conversazioni</div>
              <button className="md:hidden" onClick={()=>setMobileSidebar(false)}><X size={16}/></button>
            </div>
            <Button
              onClick={newChat}
              className="w-full rounded-full mb-3"
              data-testid="ai-new-chat"
            >
              <Plus size={14} className="mr-2"/> Nuova chat
            </Button>
            <div className="relative mb-3">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <input
                value={search}
                onChange={e=>setSearch(e.target.value)}
                placeholder="Cerca…"
                className="w-full h-9 pl-8 pr-3 rounded-lg border border-border bg-background text-xs focus:outline-none focus:border-foreground/40"
                data-testid="ai-search"
              />
            </div>
            <div className="space-y-1 max-h-[55vh] overflow-y-auto -mx-1 pr-1">
              {filteredSessions.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-4">Nessuna chat ancora</div>
              ) : (
                filteredSessions.map(s => (
                  <SessionItem
                    key={s.session_id}
                    s={s}
                    active={s.session_id === sessionId}
                    onOpen={(id)=>{ setSessionId(id); setMobileSidebar(false); }}
                    onDelete={deleteSession}
                  />
                ))
              )}
            </div>
            <GiulioMemory refreshKey={messages.length}/>
          </div>
        </aside>

        {/* ==== MAIN CHAT ==== */}
        <section className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 md:px-6 py-4 border-b border-border flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button className="md:hidden" onClick={()=>setMobileSidebar(true)} data-testid="ai-open-sidebar"><Menu size={18}/></button>
              <Sparkles size={18} className="text-chartreuse shrink-0" strokeWidth={1.6}/>
              <div className="min-w-0">
                <div className="font-display text-lg leading-tight">Assistente AgriNexus</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">Claude Sonnet 4.6 · Streaming · Multimodale</div>
              </div>
            </div>
            {/* Chip di contesto SEMPRE visibile */}
            {giulioContext && giulioContext.label ? (
              <div
                className="inline-flex items-center gap-1.5 bg-terracotta/10 border border-terracotta/30 text-terracotta rounded-full pl-3 pr-1 py-1 text-[11px] font-medium shrink-0 max-w-[260px]"
                data-testid="ai-context-chip"
              >
                <Sparkles size={10} className="shrink-0"/>
                <span className="hidden sm:inline shrink-0">Stai guardando:</span>
                <b className="truncate">{giulioContext.label}</b>
                <button
                  onClick={clearContext}
                  className="w-5 h-5 rounded-full hover:bg-terracotta/20 grid place-items-center ml-0.5 shrink-0"
                  aria-label="Rimuovi contesto"
                  data-testid="ai-context-clear"
                >
                  <X size={10}/>
                </button>
              </div>
            ) : (
              <div
                className="inline-flex items-center gap-1.5 bg-secondary border border-border text-muted-foreground rounded-full px-3 py-1 text-[11px] font-medium shrink-0 max-w-[220px]"
                data-testid="ai-context-chip"
              >
                <Sparkles size={10} className="shrink-0"/>
                <span className="hidden sm:inline shrink-0">Pagina:</span>
                <b className="truncate text-foreground">{pathToLabel(location.pathname)}</b>
              </div>
            )}
          </div>

          {/* Messages OR Empty state with templates */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {!hasMessages ? (
              <div className="max-w-2xl mx-auto py-6">
                {/* Giulio welcome bubble (static greeting) */}
                <div className="flex gap-3 mb-6" data-testid="giulio-welcome-bubble">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-chartreuse/15 border border-chartreuse/30 grid place-items-center">
                    <Sparkles size={16} className="text-chartreuse"/>
                  </div>
                  <div className="flex-1">
                    <div className="rounded-2xl rounded-tl-sm bg-secondary/60 border border-border px-4 py-3 text-sm leading-relaxed">
                      <div className="font-medium">Ciao! Sono <b className="text-chartreuse">Giulio</b>, l'assistente AI di <b>AgriNexus</b>.</div>
                      <div className="mt-2 text-muted-foreground">
                        Posso aiutarti con: <b className="text-foreground">colture, normative agricole italiane, PAC, stima quintali, valutazione terreni</b>, lettura di <b className="text-foreground">visure catastali</b> e foto di campi. Posso anche spiegarti come funziona la piattaforma — pubblicare un terreno, cercare partite, usare Yield AI PRO.
                      </div>
                      <div className="mt-2 text-[11px] text-muted-foreground">💡 Puoi scrivere, allegare una foto o usare il microfono.</div>
                    </div>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-chartreuse/20 text-obsidian text-[10px] uppercase tracking-widest font-bold mb-4 dark:text-chartreuse">
                    <Sparkles size={11}/> Powered by Claude Sonnet 4.6
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl tracking-tighter">
                    Come posso aiutarti oggi?
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Assistente specializzato in agricoltura italiana. Chiedi, allega foto o mappe, usa il microfono.
                  </p>
                </div>

                {/* Prompt templates */}
                <div className="space-y-3">
                  {templates.map((g, gi) => (
                    <div key={gi} className="rounded-xl border border-border overflow-hidden">
                      <button
                        onClick={()=>setOpenGroup(o => o === gi ? -1 : gi)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/50 transition"
                        data-testid={`ai-template-group-${gi}`}
                      >
                        <span className="font-medium text-sm">{g.group}</span>
                        {openGroup === gi ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                      {openGroup === gi && (
                        <div className="px-2 pb-2 space-y-1">
                          {g.items.map((it, ii) => (
                            <button
                              key={ii}
                              onClick={()=>setText(it.prompt)}
                              data-testid={`ai-template-${gi}-${ii}`}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary transition"
                            >
                              <div className="text-sm font-medium">{it.title}</div>
                              <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.prompt}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((m, i) => {
                  const isLastAssistant = m.role === 'assistant' && i === messages.length - 1;
                  // Se questo messaggio è in streaming, mostra il testo progressivo
                  const displayMsg = m._streaming
                    ? { ...m, content: streamingText }
                    : m;
                  return (
                    <Bubble
                      key={i} m={displayMsg} i={i}
                      onCopy={copyText}
                      onRegenerate={regenerate}
                      isLastAssistant={isLastAssistant && !busy && !m._streaming}
                    />
                  );
                })}
                {busy && (
                  <div className="flex justify-start mb-4">
                    <div className="rounded-2xl px-4 py-3 bg-secondary border border-border inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-chartreuse animate-pulse"></span>
                      <span className="w-2 h-2 rounded-full bg-chartreuse animate-pulse" style={{animationDelay:'0.2s'}}></span>
                      <span className="w-2 h-2 rounded-full bg-chartreuse animate-pulse" style={{animationDelay:'0.4s'}}></span>
                      <span className="text-xs text-muted-foreground ml-1">Sto pensando…</span>
                    </div>
                  </div>
                )}
                {streamingIdx >= 0 && (
                  <div className="flex justify-center mb-2">
                    <button
                      onClick={stopStreaming}
                      data-testid="ai-stop-streaming"
                      className="text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1 bg-background/60 backdrop-blur"
                    >
                      Mostra tutto subito
                    </button>
                  </div>
                )}
                <div ref={endRef}/>
              </>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-border px-3 md:px-4 py-3">
            <div className="text-[10px] text-muted-foreground pb-2 flex items-center gap-1">
              <Info size={10}/> Supporto informativo, non sostituisce un agronomo/notaio qualificato.
            </div>

            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 pb-2">
                {attachments.map((a, i) => (
                  <div key={i} className="relative">
                    <img src={a.dataUrl} alt="" className="w-14 h-14 rounded-lg object-cover border border-border"/>
                    <button
                      onClick={()=>removeAttachment(i)}
                      aria-label="Rimuovi"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground text-background grid place-items-center"
                    ><X size={10}/></button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-end">
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                onChange={e=>handleFiles(e.target.files)} data-testid="ai-file-input"/>
              <Button type="button" variant="outline" size="icon"
                onClick={()=>fileInputRef.current?.click()}
                disabled={busy || processingImage || attachments.length >= MAX_IMAGES}
                className="rounded-full h-11 w-11 shrink-0" title="Allega immagini"
                data-testid="ai-attach-btn"
              ><ImagePlus size={16}/></Button>

              <Button type="button" variant="outline" size="icon"
                onClick={recording ? stopVoice : startVoice}
                disabled={busy}
                className={`rounded-full h-11 w-11 shrink-0 ${recording ? 'bg-terracotta text-white border-terracotta' : ''}`}
                title="Detta con la voce"
                data-testid="ai-voice-btn"
              >{recording ? <MicOff size={16}/> : <Mic size={16}/>}</Button>

              <Textarea
                rows={2}
                value={text}
                onChange={e=>setText(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); send(); } }}
                placeholder={attachments.length ? 'Descrivi cosa vuoi analizzi…' : recording ? 'Sto ascoltando…' : 'Scrivi o detta la tua domanda…'}
                className="flex-1"
                data-testid="ai-chat-input"
              />

              <Button
                onClick={()=>send()}
                disabled={busy || processingImage || (!text.trim() && attachments.length === 0)}
                className="rounded-full h-11 w-11 p-0 self-end"
                data-testid="ai-send-btn"
              ><Send size={16}/></Button>
            </div>
            {processingImage && <div className="text-[10px] text-muted-foreground pt-1.5 animate-pulse">Elaborazione immagine…</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
